let ctx = null;
export const getCtx = () => ctx;

export const initSfx = () => {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume();
    loadSfx();
  } catch (e) {
    ctx = null;
  }
};

const SFX_NAMES = [
  "diode1_impact", "diode2_impact", "boot_drone", "boot_riser", "glitch_a", "glitch_b", "glitch_c",
  "warn_sting", "heartbeat", "initiating_braam", "established_hum",
];
const raw = {};
const buffers = {};
const live = new Set();
let master = null;
let decoded = null;
window.__sporeSfx = { played: [], loaded: () => Object.keys(buffers) };

export const prefetchSfx = () => {
  SFX_NAMES.forEach((n) => {
    raw[n] = raw[n] || fetch(`/sfx/${n}.mp3`, { priority: "low" }).then((r) => r.arrayBuffer()).catch(() => null);
  });
};

const out = () => {
  if (!master) {
    if (!ctx.createDynamicsCompressor) return ctx.destination;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -10;
    comp.knee.value = 6;
    comp.ratio.value = 12;
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    master = ctx.createGain();
    master.gain.value = 0.85;
    master.connect(comp).connect(ctx.destination);
  }
  return master;
};

export const loadSfx = () => {
  if (!ctx) return Promise.resolve();
  if (decoded) return decoded;
  prefetchSfx();
  decoded = Promise.all(
    SFX_NAMES.map(async (n) => {
      const ab = await raw[n];
      if (!ab) return;
      try {
        buffers[n] = await ctx.decodeAudioData(ab.slice(0));
      } catch (e) {
        /* missing sfx is non-fatal */
      }
    }),
  );
  return decoded;
};

// Source nodes only stop if they started, and only once; never throws.
const safeStart = (node, when = 0) => {
  try {
    node.start(when);
    node.__started = true;
  } catch (e) {
    node.__started = false;
  }
  return node.__started;
};

const safeStop = (node, when = 0) => {
  if (!node || !node.__started || node.__stopped) return;
  node.__stopped = true;
  try {
    node.stop(when);
  } catch (e) {
    /* already stopped */
  }
};

const fadeOutGain = (g, dur) => {
  try {
    const t = ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  } catch (e) {
    /* ignore */
  }
};

export const playSfx = async (name, { gain = 1, fadeIn = 0, loop = false } = {}) => {
  try {
    if (!ctx) return null;
    if (!buffers[name]) await loadSfx();
    const buf = buffers[name];
    if (!buf) return null;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(fadeIn ? 0.0001 : gain, t);
    if (fadeIn) g.gain.exponentialRampToValueAtTime(gain, t + fadeIn);
    src.connect(g).connect(out());
    if (!safeStart(src)) return null;
    const h = { src, g, fading: false };
    live.add(h);
    src.onended = () => live.delete(h);
    window.__sporeSfx.played.push(name);
    return h;
  } catch (e) {
    return null;
  }
};

export const fadeSfx = (h, dur = 0.5) => {
  if (!h || !ctx || h.fading) return;
  h.fading = true;
  fadeOutGain(h.g, dur);
  try {
    safeStop(h.src, ctx.currentTime + dur + 0.05);
  } catch (e) {
    /* ignore */
  }
  live.delete(h);
};

const loops = new Set();

const equalPower = (fadeIn) => {
  const c = new Float32Array(64);
  for (let i = 0; i < 64; i++) {
    const x = i / 63;
    c[i] = Math.max(0.0001, fadeIn ? Math.sin((x * Math.PI) / 2) : Math.cos((x * Math.PI) / 2));
  }
  return c;
};

// Seamless loop: overlapping copies with equal-power crossfades, so the file's seam is never heard.
export const playLoop = async (name, { gain = 0.3, xfade = 4, lowpass = 900 } = {}) => {
  try {
    if (!ctx) return null;
    if (!buffers[name]) await loadSfx();
    const buf = buffers[name];
    if (!buf) return null;
    const fade = Math.min(xfade, buf.duration / 3);
    const period = buf.duration - fade;
    const bus = ctx.createGain();
    bus.gain.value = gain;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = lowpass;
    bus.connect(lp).connect(out());
    const st = { alive: true, iterations: 0, timer: null, srcs: new Set() };
    const schedule = (when) => {
      if (!st.alive) return;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      g.gain.setValueCurveAtTime(equalPower(true), when, fade);
      g.gain.setValueCurveAtTime(equalPower(false), when + period, fade - 0.01);
      src.connect(g).connect(bus);
      if (safeStart(src, when)) st.srcs.add(src);
      src.onended = () => st.srcs.delete(src);
      st.iterations += 1;
      const next = when + period;
      st.timer = setTimeout(() => schedule(next), Math.max(0, (next - ctx.currentTime - 2) * 1000));
    };
    schedule(ctx.currentTime + 0.05);
    const h = {
      name, state: st,
      stop: (dur = 0.4) => {
        if (!st.alive) return;
        st.alive = false;
        clearTimeout(st.timer);
        fadeOutGain(bus, dur);
        st.srcs.forEach((x) => safeStop(x, ctx.currentTime + dur + 0.05));
        loops.delete(h);
      },
    };
    loops.add(h);
    window.__sporeSfx.loops = () => Array.from(loops).map((l) => ({ name: l.name, iterations: l.state.iterations, playing: l.state.srcs.size }));
    window.__sporeSfx.played.push(`${name}:loop`);
    return h;
  } catch (e) {
    return null;
  }
};

export const stopAllSfx = (dur = 0.3) => {
  Array.from(live).forEach((h) => fadeSfx(h, dur));
  Array.from(loops).forEach((h) => h.stop(dur));
};

const tone = (freq, dur, type = "square", vol = 0.03) => {
  try {
    if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(out());
    if (safeStart(o, t)) safeStop(o, t + dur + 0.05);
  } catch (e) {
    /* ignore */
  }
};

export const beep = (f = 880) => tone(f, 0.05, "square", 0.018);

export const glitchBurst = () => {
  try {
    if (!ctx) return;
    const len = Math.floor(ctx.sampleRate * 0.5);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) * (Math.random() > 0.7 ? 1 : 0.2);
    const src = ctx.createBufferSource();
    const g = ctx.createGain();
    g.gain.value = 0.12;
    src.buffer = buf;
    src.connect(g).connect(out());
    safeStart(src);
  } catch (e) {
    /* ignore */
  }
};
